import { useEffect } from 'react'

import * as Sentry from '@sentry/nextjs'

import { useIncidentExtendedClipStatusQuery } from '@/graphql/generated/hooks'

export interface UseIncidentExtendedClipStatusResultIProps {
  /**
   * Whether the incident has an EXTENDED (forensic) clip right now. `undefined` when availability
   * could not be determined — the request is still in flight or errored — so callers must omit
   * `has_extended_clip` rather than coerce this to a boolean. `false` is returned only after a
   * successful availability response, keeping a real "no extended clip" distinct from a
   * query/telemetry failure.
   */
  hasExtendedClip: boolean | undefined
  /**
   * True once the availability probe has settled (loaded, errored, or was skipped). The drawer-open
   * analytics event waits on this so `has_extended_clip` reflects a real "available at open" answer
   * rather than the transient `false` seen while the query is still in flight. An error also counts
   * as settled (availability is unknown, surfaced via `hasExtendedClip === undefined`) so a failing
   * probe never blocks the drawer-open event.
   */
  isReady: boolean
}

/**
 * Standalone probe for the incident's EXTENDED (forensic) clip, used only to populate the
 * `has_extended_clip` drawer-open analytics property. It runs a *dedicated* lean query
 * (`IncidentExtendedClipStatus`) that selects only `incident.clips.totalCount` — the full
 * `IncidentVideo` query would return `incident.video` plus every clip's times and presigned URLs
 * (~3.5 KB, with URLs the backend must pre-sign only for us to discard).
 *
 * `Incident.clips` is EXTENDED-only: the backend backs it with the `incident_extended_clip` table,
 * while the ORIGINAL video is served via top-level `incident.video` and never enters `clips`. So a
 * positive `totalCount` means an EXTENDED clip exists — no need to fetch or inspect individual clips
 * (the `id`-only alternative would still transfer edge objects, and requesting any `video` URL would
 * make the backend pre-sign it).
 *
 * The hook returns *only* booleans, keeping the clips type out of `IncidentDrawer`'s type context
 * (TS2590 union-complexity ceiling).
 */
export const useIncidentExtendedClipStatus = (
  incidentId: string | undefined,
  skip: boolean
): UseIncidentExtendedClipStatusResultIProps => {
  const {
    data,
    loading: isLoading,
    error,
  } = useIncidentExtendedClipStatusQuery({
    variables: { id: incidentId ?? '' },
    skip: skip || !incidentId,
    // Extended-clip availability changes as clips are generated after the incident, so each drawer
    // open must reflect current availability. `cache-first` would serve a stale `false` cached
    // before the forensic clip landed, reporting the wrong `has_extended_clip` on later opens.
    // Mirrors `useIncidentVideo`, which uses `no-cache` on its own query for the same reason.
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  })

  // Report the probe error so telemetry failures are visible rather than silently swallowed by
  // `errorPolicy: 'all'`. Done in an effect (not the query's `onError`) because `errorPolicy: 'all'`
  // delivers the error in the result instead of reliably invoking `onError`.
  useEffect(() => {
    if (error) {
      Sentry.captureException(error)
    }
  }, [error])

  // Availability is known only from a successful response. While in flight or on error, return
  // `undefined` (unknown) so callers omit `has_extended_clip` instead of reporting a misleading
  // `false` that would be indistinguishable from a real "no extended clip exists".
  const hasExtendedClip =
    isLoading || error
      ? undefined
      : (data?.incident?.clips?.totalCount ?? 0) > 0

  return { hasExtendedClip, isReady: !isLoading }
}
